/**
 * Service for managing AI applications available for deployment
 */
class AIAppsService {
  constructor() {
    // Initialize with all available AI apps
    this.availableApps = [
      {
        id: 'lesson-plan-creator',
        name: 'Lesson Plan Creator',
        description: 'Creates ready-to-use lesson plans for teachers to save time and engage students',
        category: 'education',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-lesson-plan', method: 'POST', description: 'Generate a lesson plan' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'includeActivities', name: 'Include Activities', type: 'boolean', default: true },
          { id: 'gradeLevel', name: 'Grade Level', type: 'select', options: ['elementary', 'middle', 'high', 'college'], default: 'elementary' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'course-outline-creator',
        name: 'Course Outline Creator',
        description: 'Creates organized learning plans with clear goals for teachers to guide their lessons',
        category: 'education',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-course-outline', method: 'POST', description: 'Generate a course outline' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'includeAssessments', name: 'Include Assessments', type: 'boolean', default: true },
          { id: 'courseLength', name: 'Course Length', type: 'select', options: ['semester', 'year', 'quarter'], default: 'semester' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'sql-writer',
        name: 'SQL Writer',
        description: 'Generate optimized SQL queries with AI assistance',
        category: 'development',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-sql', method: 'POST', description: 'Generate SQL query' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'databaseType', name: 'Database Type', type: 'select', options: ['postgresql', 'mysql', 'sqlserver', 'oracle'], default: 'postgresql' },
          { id: 'optimizationFocus', name: 'Optimization Focus', type: 'select', options: ['performance', 'readability', 'compatibility'], default: 'performance' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'pyspark-writer',
        name: 'PySpark Writer',
        description: 'Create efficient PySpark transformations and analytics code',
        category: 'development',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-pyspark', method: 'POST', description: 'Generate PySpark code' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'sparkVersion', name: 'Spark Version', type: 'select', options: ['2.4', '3.0', '3.1', '3.2', '3.3'], default: '3.3' },
          { id: 'optimizationLevel', name: 'Optimization Level', type: 'select', options: ['basic', 'advanced'], default: 'advanced' }
        ],
        dockerConfig: {
          baseImage: 'python:3.9-slim',
          port: 5000,
          memoryNeeded: '2GB'
        }
      },
      {
        id: 'social-media-manager',
        name: 'Social Media Manager',
        description: 'Create and manage social media content with AI assistance',
        category: 'marketing',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-post', method: 'POST', description: 'Generate social media post' },
          { path: '/api/schedule-post', method: 'POST', description: 'Schedule a post' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'platforms', name: 'Platforms', type: 'multiselect', options: ['twitter', 'linkedin', 'facebook', 'instagram'], default: ['twitter', 'linkedin'] },
          { id: 'postFrequency', name: 'Post Frequency', type: 'select', options: ['daily', 'weekly', 'custom'], default: 'weekly' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'creative-writer',
        name: 'Content Studio',
        description: 'Transform ideas into polished professional communications with AI assistance',
        category: 'marketing',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-content', method: 'POST', description: 'Generate content' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'contentType', name: 'Content Type', type: 'select', options: ['blog', 'email', 'social', 'report'], default: 'blog' },
          { id: 'tone', name: 'Tone', type: 'select', options: ['professional', 'casual', 'technical', 'persuasive'], default: 'professional' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'powerpoint-generator',
        name: 'Slide Generator',
        description: 'Generate professional presentations with AI assistance',
        category: 'marketing',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-slides', method: 'POST', description: 'Generate presentation slides' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'slideCount', name: 'Slide Count', type: 'number', min: 5, max: 30, default: 10 },
          { id: 'theme', name: 'Theme', type: 'select', options: ['corporate', 'minimal', 'creative', 'academic'], default: 'corporate' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'play-formatter',
        name: 'Play Formatter',
        description: 'Transform raw text into properly formatted play scripts and screenplays',
        category: 'writing',
        isAI: true,
        apiEndpoints: [
          { path: '/api/format-play', method: 'POST', description: 'Format play script' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'formatType', name: 'Format Type', type: 'select', options: ['screenplay', 'stageplay', 'radio'], default: 'screenplay' },
          { id: 'includeNotes', name: 'Include Notes', type: 'boolean', default: true }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'data-dictionary-generator',
        name: 'Data Dictionary Generator',
        description: 'Generate comprehensive data dictionaries and sample data with AI assistance',
        category: 'development',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-data-dictionary', method: 'POST', description: 'Generate data dictionary' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'dataFormat', name: 'Data Format', type: 'select', options: ['csv', 'json', 'sql'], default: 'csv' },
          { id: 'includeSampleData', name: 'Include Sample Data', type: 'boolean', default: true }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'training-manual-creator',
        name: 'Training Manual Creator',
        description: 'Create comprehensive training manuals with AI assistance and PDF export',
        category: 'education',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-training-manual', method: 'POST', description: 'Generate training manual' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'includeQuizzes', name: 'Include Quizzes', type: 'boolean', default: true },
          { id: 'manualType', name: 'Manual Type', type: 'select', options: ['onboarding', 'technical', 'compliance', 'customer-service'], default: 'onboarding' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'research-assistant',
        name: 'Research Assistant',
        description: 'Generate comprehensive research overviews with authoritative sources',
        category: 'research',
        isAI: true,
        apiEndpoints: [
          { path: '/api/research-topic', method: 'POST', description: 'Research a topic' }
        ],
        requiredApiKeys: ['gemini_api_key', 'tavily_api_key'],
        configOptions: [
          { id: 'citationStyle', name: 'Citation Style', type: 'select', options: ['apa', 'mla', 'chicago', 'harvard'], default: 'apa' },
          { id: 'researchDepth', name: 'Research Depth', type: 'select', options: ['basic', 'intermediate', 'comprehensive'], default: 'comprehensive' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'grant-writer',
        name: 'Grant Writer',
        description: 'Get AI help writing compelling grant proposals',
        category: 'nonprofit',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-proposal', method: 'POST', description: 'Generate grant proposal' }
        ],
        requiredApiKeys: ['anthropic_api_key'],
        configOptions: [
          { id: 'grantType', name: 'Grant Type', type: 'select', options: ['federal', 'foundation', 'corporate', 'research'], default: 'foundation' },
          { id: 'includeTimeline', name: 'Include Timeline', type: 'boolean', default: true }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'grant-finder',
        name: 'Grant Finder',
        description: 'Discover grants tailored to your organization using AI matching',
        category: 'nonprofit',
        isAI: true,
        apiEndpoints: [
          { path: '/api/find-grants', method: 'POST', description: 'Find matching grants' }
        ],
        requiredApiKeys: ['gemini_api_key', 'tavily_api_key'],
        configOptions: [
          { id: 'orgType', name: 'Organization Type', type: 'select', options: ['nonprofit', 'education', 'research', 'government'], default: 'nonprofit' },
          { id: 'fundingAmount', name: 'Funding Amount', type: 'select', options: ['small', 'medium', 'large'], default: 'medium' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'code-reviewer',
        name: 'Code Reviewer',
        description: 'AI-powered code review and analysis',
        category: 'development',
        isAI: true,
        apiEndpoints: [
          { path: '/api/review-code', method: 'POST', description: 'Review code' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'language', name: 'Programming Language', type: 'select', options: ['javascript', 'python', 'java', 'go', 'ruby', 'php', 'csharp'], default: 'javascript' },
          { id: 'reviewDepth', name: 'Review Depth', type: 'select', options: ['basic', 'detailed', 'security-focused'], default: 'detailed' }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      },
      {
        id: 'branding-agent',
        name: 'Branding Agent',
        description: 'AI-powered branding and marketing content assistant',
        category: 'marketing',
        isAI: true,
        apiEndpoints: [
          { path: '/api/generate-brand-content', method: 'POST', description: 'Generate brand content' },
          { path: '/api/analyze-brand', method: 'POST', description: 'Analyze brand profile' }
        ],
        requiredApiKeys: ['gemini_api_key'],
        configOptions: [
          { id: 'brandVoice', name: 'Brand Voice', type: 'select', options: ['professional', 'casual', 'innovative', 'traditional'], default: 'professional' },
          { id: 'contentTypes', name: 'Content Types', type: 'multiselect', options: ['social', 'email', 'blog', 'ads'], default: ['social', 'email'] }
        ],
        dockerConfig: {
          baseImage: 'node:18-alpine',
          port: 3000,
          memoryNeeded: '1GB'
        }
      }
    ];
  }

  /**
   * Get all available AI apps
   * @returns {Array} List of all available AI apps
   */
  getAllApps() {
    return this.availableApps;
  }

  /**
   * Get AI apps by category
   * @param {string} category - Category to filter by
   * @returns {Array} List of AI apps in the specified category
   */
  getAppsByCategory(category) {
    return this.availableApps.filter(app => app.category === category);
  }

  /**
   * Get an AI app by ID
   * @param {string} id - ID of the app to retrieve
   * @returns {Object|null} The app object or null if not found
   */
  getAppById(id) {
    return this.availableApps.find(app => app.id === id) || null;
  }

  /**
   * Get the Docker configuration for an app
   * @param {string} id - ID of the app
   * @returns {Object|null} Docker configuration or null if not found
   */
  getDockerConfig(id) {
    const app = this.getAppById(id);
    return app ? app.dockerConfig : null;
  }

  /**
   * Get the required API keys for an app
   * @param {string} id - ID of the app
   * @returns {Array|null} List of required API keys or null if not found
   */
  getRequiredApiKeys(id) {
    const app = this.getAppById(id);
    return app ? app.requiredApiKeys : null;
  }

  /**
   * Get the configuration options for an app
   * @param {string} id - ID of the app
   * @returns {Array|null} List of configuration options or null if not found
   */
  getConfigOptions(id) {
    const app = this.getAppById(id);
    return app ? app.configOptions : null;
  }

  /**
   * Get the API endpoints for an app
   * @param {string} id - ID of the app
   * @returns {Array|null} List of API endpoints or null if not found
   */
  getApiEndpoints(id) {
    const app = this.getAppById(id);
    return app ? app.apiEndpoints : null;
  }
}

// Export singleton instance
const aiAppsService = new AIAppsService();
export default aiAppsService; 